import type { ResponsiveValue } from './types';

const isResponsiveValue = <T,>(
  value: T | ResponsiveValue<T>
): value is ResponsiveValue<T> => {
  return typeof value === 'object' && value !== null && 'base' in value;
};

export const getResponsiveAttributes = <
  T extends string | number,
  U extends Record<T, string>
>(
  property: string,
  values: T | ResponsiveValue<T> | undefined,
  mapping?: U
): Record<string, T | U[keyof U]> | undefined => {
  if (!values && values !== 0) return undefined;

  const attributeKey = `data-${property}`;
  if (isResponsiveValue(values)) {
    const { base, ...rest } = values;

    const res = {
      ...Object.fromEntries(
        Object.entries(rest).map(([size, token]) => {
          return [
            `${attributeKey}-responsive-${size}`,
            mapping?.[token] ?? token,
          ];
        })
      ),
      [attributeKey]: mapping?.[base] ?? base,
    };
    return res;
  }
  return {
    [attributeKey]: mapping?.[values] ?? values,
  };
};

export const spacingMap = {
  0: '0',
  4: '100',
  8: '200',
  10: 'xs',
  12: '300',
  16: '400',
  20: '500',
  24: '600',
  32: '700',
  40: '800',
} as const;
